import Reveal from '@/components/base/Reveal';

const described = [
  {
    label: 'Instrument',
    value: 'XAUUSD',
    note: 'The one market the strategy trades — gold priced against the US dollar.',
  },
  {
    label: 'Platform',
    value: 'MetaTrader 5',
    note: 'Where the strategy runs and where its trades are actually executed.',
  },
  {
    label: 'Decision basis',
    value: 'Predefined conditions',
    note: 'Entries come from conditions set in advance, not a live call made on the spot.',
  },
  {
    label: 'Directions',
    value: 'Long and short',
    note: 'It is willing to take either side of the market when the conditions line up.',
  },
  {
    label: 'Record',
    value: 'Every closed trade',
    note: 'Every trade that finishes — winning or losing — goes into the book.',
  },
];

const notPublished = [
  {
    label: 'Internal parameter values',
    note: 'The numbers that tune how sensitive each condition is.',
  },
  {
    label: 'Specific entry and exit thresholds',
    note: 'The exact price or reading that triggers a buy or a sell.',
  },
  {
    label: 'Position sizing configuration',
    note: 'The rules that decide how much is risked on each trade.',
  },
  {
    label: 'Proprietary condition logic',
    note: 'The step-by-step logic that links the conditions together.',
  },
];

function ScoringScope() {
  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full border border-background-300 bg-background-100 mb-6">
            <i className="ri-shield-check-line text-[14px] text-primary-600"></i>
            <span className="text-[11px] font-data tracking-[0.16em] text-foreground-600">
              DOCUMENTATION SCOPE
            </span>
          </div>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[42px] lg:text-[46px]">
            A Framework, Not A Black Box.
          </h2>
          <p className="mt-5 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[520px]">
            This page walks through how the strategy is actually put together: one market,
            condition-based entries, both directions, and a book that records every trade. What it
            deliberately leaves out is the tuning — the exact thresholds, the parameter values, and
            the weighting behind each condition. Those stay private.
          </p>
          <p className="mt-4 text-[14px] leading-relaxed text-foreground-500 max-w-[520px]">
            That is a normal line to draw. You can see what the system does and judge it on its
            results without being handed the settings that make it work. And the part that can be
            checked — the full trade record, winners and losers alike — is published in full.
          </p>
        </Reveal>

        <Reveal delay={140}>
          <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            <div className="px-5 md:px-6 py-4 border-b border-background-200 bg-background-100">
              <span className="text-[12px] font-data tracking-widest text-foreground-700">
                WHAT THIS PAGE COVERS
              </span>
            </div>

            <div className="px-5 md:px-6 py-2">
              {described.map((row) => (
                <div
                  key={row.label}
                  className="py-3.5 border-b border-background-200 last:border-b-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground-900">{row.label}</p>
                      <p className="mt-1 text-[12px] leading-snug text-foreground-500">
                        {row.note}
                      </p>
                    </div>
                    <span className="flex items-center gap-2 shrink-0 pt-0.5">
                      <i className="ri-check-line text-[14px] text-primary-600"></i>
                      <span className="text-[12.5px] font-data text-foreground-900 whitespace-nowrap">
                        {row.value}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 md:px-6 py-4 border-t border-background-200 bg-background-100">
              <span className="text-[11px] font-data tracking-[0.16em] text-foreground-500">
                NOT PUBLISHED HERE
              </span>
              <div className="mt-3 space-y-2.5">
                {notPublished.map((item) => (
                  <div key={item.label} className="flex items-start gap-2.5">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-subtract-line text-[12px] text-foreground-400"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-data text-foreground-600">{item.label}</p>
                      <p className="mt-0.5 text-[12px] leading-snug text-foreground-400">
                        {item.note}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default ScoringScope;